<script lang="ts">
	/**
	 * Products, and the **write-once `entitlementKey`** (Design §4.1).
	 *
	 * The field renders read-only with an explanation once `publishedAt` is set. That is a courtesy,
	 * not the guarantee: `CatalogService` refuses the change, so a seed script, a CLI or a hand-rolled
	 * request is refused too. The form mirrors the service rule rather than inventing its own.
	 */

	import {
		Badge,
		Button,
		Checkbox,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Textarea,
		Toggle
	} from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import {
		ComparisonFeatureEditor,
		ConfirmButton,
		CurrencyPicker,
		DragHandle,
		DragSort,
		EmptyState,
		FormField,
		FormModal,
		ImageField,
		Panel,
		PostPurchaseEditor,
		PriceDisplay,
		SelectField
	} from '../../../components';
	import { createPrice, createProduct, reorderProducts, updateProduct } from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import {
		BILLING_INTERVALS,
		formatFriendlyDate,
		formatMoney,
		isArchivedProduct,
		majorUnitsFrom,
		mediaURL,
		minorUnitsFrom,
		moveKeyBefore,
		moveKeyBy,
		postPurchaseDraftOf,
		postPurchaseInput
	} from '../../../Utilities';
	import type {
		BillingInterval,
		CatalogSyncStatusItem,
		Product,
		ProductFamily,
		AdminClickwrap,
		AdminProductOption,
		ProductComparisonFeature,
		Seller,
		StoreApplication,
		StorefrontCurrency
	} from '../../../AppTypes';

	export let products: Product[] = [];
	export let families: ProductFamily[] = [];
	/**
	 * Whether archived products are listed (§4.1). Owned by the catalogue page, which drives the
	 * families panel from the same switch — "archived" is one idea, and two would disagree.
	 */
	export let showArchived: boolean = false;
	/**
	 * The legal entities that can sell a product (§4.6). Empty until a store configures one, which
	 * is the state a store starts in — the selector then offers only the default.
	 */
	export let sellers: Seller[] = [];
	/**
	 * Registered payment adapter keys — `['stripe']`.
	 *
	 * A tax code is **provider vocabulary**: Stripe's `txcd_…` string, not ours. Rendering one
	 * field per adapter is what keeps that honest, and what lets a second provider carry its own
	 * without a schema change.
	 */
	export let providerTypes: string[] = [];
	/**
	 * The terms that exist, so a product can be ticked into the ones that apply to it (§4.7).
	 *
	 * Ticking one here attaches **this product** to that term and nothing else: a term covering
	 * four plans keeps the other three.
	 */
	export let clickwraps: AdminClickwrap[] = [];
	/**
	 * The options that exist, so a product can be ticked into the ones offered on it (§4.8).
	 *
	 * Ticking one attaches **this product** and nothing else, exactly as a term does. The one word
	 * of difference runs through the copy on this form too: these are offered, not required.
	 */
	export let productOptions: AdminProductOption[] = [];
	/**
	 * The registered applications, so a product can say **who consumes it** (§4.3).
	 *
	 * The same fact as `Application.subscribedEntitlementKeys`, asked from this end. Without it the
	 * only way to make a new product visible to the application that reads it is to note its
	 * entitlement key, leave for `/Settings/Admin/Applications`, and type the string back in from memory —
	 * and a key that is mistyped there scopes the integration to a key no product has, which reads
	 * as configured and returns nothing.
	 *
	 * Empty is the ordinary state of a store with no integrations, and the field then renders
	 * nothing at all rather than an empty box.
	 */
	export let applications: StoreApplication[] = [];
	export let currency: StorefrontCurrency | undefined = undefined;
	/**
	 * Every enabled currency, so the create form can carry a first price.
	 *
	 * A product with no price is unbuyable and the store stays shut until one has one, so making
	 * the price a separate screen means every new product spends time in a state the admin has to
	 * remember to leave.
	 */
	export let currencies: StorefrontCurrency[] = [];
	export let loading: boolean = false;
	/** The product whose prices the sibling panel is showing, bindable. */
	export let selectedProductUUID: string = '';

	/**
	 * Product uuid → its state at the payment provider (Design §5.3).
	 *
	 * Catalogue sync is manual, so a previously-failed push is otherwise invisible until somebody
	 * happens to sync again — and the only symptom is a Buy button that 500s. A product missing
	 * from this map has not been looked up rather than being fine, so nothing is rendered for it.
	 */
	export let syncStatus: Map<string, CatalogSyncStatusItem> = new Map();

	/** Stated in words as well as colour, so the badge survives a monochrome reading. */
	const syncCopy: Record<CatalogSyncStatusItem['syncStatus'], { label: string; classes: string }> = {
		synced: { label: 'Synced', classes: 'text-green-700 dark:text-green-400' },
		pending: { label: 'Sync pending', classes: 'text-amber-700 dark:text-amber-400' },
		failed: { label: 'Sync failed', classes: 'text-red-600 dark:text-red-400' },
		never_synced: { label: 'Never synced', classes: 'text-amber-700 dark:text-amber-400' }
	};

	const dispatcher = createEventDispatcher();

	let isAdding: boolean = false;
	let saving: boolean = false;
	let busyUUID: string = '';
	let editingUUID: string = '';

	function emptyDraft() {
		return {
			slug: '',
			name: '',
			type: 'subscription',
			familySlug: '',
			sellerUUID: '',
			rank: 0,
			entitlementKey: '',
			// The presentation fields, which used to be reachable only by creating the product and
			// then editing it — so the first thing every new product did was appear with no
			// tagline, no description and no features.
			tagline: '',
			description: '',
			highlight: '',
			comparisonFeatures: [] as ProductComparisonFeature[],
			isPublic: true,
			// §4.1. Off is the licence — a second purchase of something already owned is refused.
			isRepeatable: false,
			// §4.1. Text, like the refund window, so a half-typed figure is not coerced to zero.
			seatsPerUnit: '1',
			refundWindowDays: '',
			clickwrapUUIDs: [] as string[],
			optionUUIDs: [] as string[],
			applicationUUIDs: [] as string[],
			imageUUID: null as string | null,
			imageURL: null as string | null,
			/*
			 * The first price, written straight after the product in the same submit.
			 *
			 * Optional, because a product can legitimately be created before its pricing is
			 * decided — but on by default, because the alternative is a product nobody can buy and
			 * a store that stays shut until somebody notices.
			 */
			withPrice: true,
			priceInterval: 'month',
			priceIntervalCount: 1,
			priceTaxBehavior: 'exclusive',
			priceTrialDays: 0,
			priceBaseCurrency: '',
			priceAmounts: {} as Record<string, string>
		};
	}

	/**
	 * Rows on their way to the server, with the unfinished ones dropped.
	 *
	 * A row with no label has no key for the storefront to align columns on, so it would render as
	 * a nameless row that dashes against every other tier — visible, meaningless and impossible to
	 * remove from any screen but this one. An admin who added a row and then thought better of it
	 * gets it discarded rather than published.
	 */
	function cleanComparisonRows(rows: ProductComparisonFeature[]): ProductComparisonFeature[] {
		return rows
			.filter(row => row.label.trim().length > 0)
			.map(row => ({
				group: row.group === null || row.group.trim().length === 0 ? null : row.group.trim(),
				label: row.label.trim(),
				// Blank is the tick, and it is stored as null so the storefront never has to
				// distinguish "included" from "included, amount unstated".
				value: row.value === null || row.value.trim().length === 0 ? null : row.value.trim(),
				// Sent only when it is ticked. The whole array is replaced on every save, so an
				// unticked row is a row that arrives without the key — which is how unticking one
				// takes effect, and why absent has to mean false on both sides rather than
				// "unchanged". Written out in full it would be `false` on almost every row of
				// every product, for a field the invoice reads by asking whether it is `true`.
				...(row.includeOnInvoice === true ? { includeOnInvoice: true } : {})
			}));
	}

	/**
	 * The rows and sections the rest of the line already uses.
	 *
	 * Offered as a datalist rather than enforced: the label is what the storefront aligns columns
	 * on, so "Projects" and "Project limit" are two rows that each dash against the other — which
	 * looks exactly like two real features. Suggesting the existing spelling is the cheapest thing
	 * that makes the right one the easy one.
	 */
	function siblingRows(all: Product[], familySlug: string, exceptUUID: string): ProductComparisonFeature[] {
		return all
			.filter(candidate => candidate.familySlug === familySlug && candidate.uuid !== exceptUUID)
			.flatMap(candidate => candidate.comparisonFeatures ?? []);
	}

	/** What the rest of the line already calls its rows, for each form's suggestions. */
	$: draftSiblingRows = siblingRows(products, draft.familySlug, '');
	$: editSiblingRows = siblingRows(products, edit.familySlug, editingUUID);

	let draft = emptyDraft();
	let edit = {
		name: '',
		tagline: '',
		description: '',
		rank: 0,
		entitlementKey: '',
		highlight: '',
		comparisonFeatures: [] as ProductComparisonFeature[],
		isPublic: true,
		isRepeatable: false,
		seatsPerUnit: '1',
		sellerUUID: '',
		familySlug: '',
		refundWindowDays: '',
		taxCodes: {} as Record<string, string>,
		clickwrapUUIDs: [] as string[],
		optionUUIDs: [] as string[],
		applicationUUIDs: [] as string[],
		imageUUID: null as string | null,
		imageURL: null as string | null,
		postPurchase: postPurchaseDraftOf(null)
	};
	let postPurchaseValid: boolean = true;

	$: familyItems = [{ value: '', name: 'No family (standalone)' }].concat(
		families.map(family => ({ value: family.slug, name: family.name }))
	);
	// Empty is a real choice and the common one: "the store's default seller", named so an admin
	// can see which entity that is rather than having to remember.
	$: sellerItems = [
		{
			value: '',
			name: `Default seller${defaultSellerName === null ? '' : ` (${defaultSellerName})`}`
		}
	].concat(sellers.map(seller => ({ value: seller.uuid, name: seller.legalName })));
	$: defaultSellerName = sellers.find(seller => seller.isDefault)?.legalName ?? null;
	$: slugError =
		draft.slug.length === 0 || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(draft.slug)
			? ''
			: 'Lowercase letters, digits and single hyphens.';
	// Whichever currency the store treats as its own, so the common case needs no choice made.
	$: if (draft.priceBaseCurrency === '' && currencies.length > 0) {
		draft.priceBaseCurrency = currencies.find(row => row.isDefault)?.code ?? currencies[0].code;
	}
	/** The amounts that are money figures. A blank or a typo is not one, and neither is sent. */
	$: draftPriceAmounts = Object.entries(draft.priceAmounts)
		.map(([code, value]) => ({
			currency: code,
			amountMinor: minorUnitsFrom(`${value ?? ''}`, fnDigitsOf(code))
		}))
		.filter((entry): entry is { currency: string; amountMinor: number } => entry.amountMinor !== null);
	$: canCreate =
		!saving &&
		slugError === '' &&
		draft.slug.length > 0 &&
		draft.name.trim().length > 0 &&
		// A price with no amount in any currency is a price nobody can be charged at, and the
		// server refuses it — so the form refuses it here rather than creating the product and
		// then failing on the second call.
		(!draft.withPrice || draftPriceAmounts.length > 0) &&
		seatsPerUnitProblem(draft.seatsPerUnit) === '';

	/**
	 * A whole number from 1 up, or the sentence saying why not. Checked here so a typo is refused on
	 * the form rather than sent as `NaN` — and never coerced, since a figure read as 1 would quietly
	 * issue one-machine licences for a product sold as two.
	 */
	function seatsPerUnitProblem(value: string): string {
		const parsed = Number(value);
		return value.trim() !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= 100_000
			? ''
			: 'A whole number of seats, 1 or more.';
	}
	$: editing = products.find(product => product.uuid === editingUUID) ?? null;
	/** The rule the service enforces, restated where the operator can see it. */
	$: isKeyFrozen = editing !== null && editing.publishedAt !== null;

	/**
	 * The decimal places a currency uses.
	 *
	 * Two is **not** the fallback. JPY has none, and assuming two turns ¥1,200 into ¥12 — silently,
	 * because both figures look like money.
	 */
	function fnDigitsOf(code: string): number {
		return currencies.find(row => row.code === code)?.minorUnitDigits ?? 0;
	}

	function fnFormatDraftAmount(code: string): string {
		const row = currencies.find(entry => entry.code === code);
		const minor = minorUnitsFrom(`${draft.priceAmounts[code] ?? ''}`, fnDigitsOf(code)) ?? 0;
		return row === undefined ? `${minor}` : formatMoney(minor, row);
	}

	/** Toggling one term, kept out of the markup so the list stays readable. */
	function fnToggleTerm(list: string[], uuid: string, event: Event): string[] {
		return (event.target as HTMLInputElement).checked
			? [...list, uuid]
			: list.filter(entry => entry !== uuid);
	}

	function fnStartEdit(product: Product): void {
		editingUUID = product.uuid;
		edit = {
			name: product.name,
			tagline: product.tagline ?? '',
			description: product.description ?? '',
			rank: product.rank,
			entitlementKey: product.entitlementKey,
			highlight: product.highlight ?? '',
			// Copied rather than aliased: the modal edits this array in place, and a cancelled
			// edit that had mutated the list row would leave the table showing changes nobody saved.
			comparisonFeatures: (product.comparisonFeatures ?? []).map(row => ({ ...row })),
			isPublic: product.isPublic,
			// Absent on a server older than the field, which is exactly what "not repeatable" was.
			isRepeatable: product.isRepeatable === true,
			seatsPerUnit: String(product.seatsPerUnit ?? 1),
			sellerUUID: product.sellerUUID ?? '',
			familySlug: product.familySlug ?? '',
			// Empty means "the store default", which is a different thing from zero days — so it
			// travels as a string and is only turned into a number when one was actually typed.
			refundWindowDays: product.refundWindowDays === null ? '' : String(product.refundWindowDays),
			taxCodes: { ...(product.taxCodes ?? {}) },
			clickwrapUUIDs: [...(product.clickwrapUUIDs ?? [])],
			optionUUIDs: [...(product.optionUUIDs ?? [])],
			// Derived from the applications' own scopes rather than from a field on the product
			// (§4.3): the scope lives on the application, and a second copy on the product DTO
			// would be a fact rendered twice that nothing keeps in step.
			applicationUUIDs: fnSubscribersOf(product.entitlementKey),
			imageUUID: product.imageUUID ?? null,
			imageURL: product.imageURL ?? null,
			postPurchase: postPurchaseDraftOf(product.postPurchase)
		};
	}

	/**
	 * The applications whose scope names this key today — the tick state the edit form opens on.
	 *
	 * `allKeys` applications are **excluded**, and shown separately as always-on. The flag is what
	 * grants them the key and their list is not consulted, so ticking one here would write a scope
	 * entry that changes nothing, and unticking one would look like access being withdrawn when it
	 * was not.
	 */
	function fnSubscribersOf(entitlementKey: string): string[] {
		return applications
			.filter(app => !app.allKeys && (app.subscribedEntitlementKeys ?? []).includes(entitlementKey))
			.map(app => app.abstractIDAppUUID);
	}

	/** The applications a form may actually tick, as against the ones that already read everything. */
	$: scopedApplications = applications.filter(app => !app.allKeys);
	$: allKeysApplications = applications.filter(app => app.allKeys);

	/**
	 * The terms a buyer of this product must tick (§4.7), split by where they come from.
	 *
	 * **The union, not the product's own list.** A term scoped to the family covers every product
	 * in the line, so a column showing only the product's own would report "no terms" for a product
	 * nobody can buy without ticking one — which is the reading that matters on this screen.
	 *
	 * The two halves are kept apart rather than merged because only one of them is editable from
	 * this row: unticking an inherited term here would do nothing, and an admin who cannot see
	 * which is which will try. Resolved from the terms list rather than from `clickwrapUUIDs`, so
	 * the inherited half needs no second field on the DTO and both panels answer the question the
	 * same way.
	 */
	function fnTermsFor(product: Product): { own: AdminClickwrap[]; inherited: AdminClickwrap[] } {
		const own = clickwraps.filter(term => term.productUUIDs.includes(product.uuid));
		const family = product.familySlug;
		return {
			own,
			inherited:
				family === null
					? []
					: clickwraps.filter(
							term => term.familySlugs.includes(family) && !own.some(seen => seen.uuid === term.uuid)
						)
		};
	}

	async function fnCreate(): Promise<void> {
		saving = true;
		const name = draft.name.trim();
		try {
			const created = await createProduct({
				slug: draft.slug,
				name,
				type: draft.type as 'subscription' | 'one_time',
				tagline: draft.tagline.trim().length === 0 ? null : draft.tagline.trim(),
				description: draft.description.trim().length === 0 ? null : draft.description.trim(),
				highlight: draft.highlight.trim().length === 0 ? null : draft.highlight.trim(),
				comparisonFeatures: cleanComparisonRows(draft.comparisonFeatures),
				isPublic: draft.isPublic,
				// Sent only for a one-time product: the server refuses it by name on a subscription,
				// and a form that submitted `false` there would be arguing about a field the admin
				// was never shown.
				...(draft.type === 'one_time' ? { isRepeatable: draft.isRepeatable } : {}),
				seatsPerUnit: Number(draft.seatsPerUnit),
				familySlug: draft.familySlug.length === 0 ? null : draft.familySlug,
				// Null means the store's default, which is a value rather than an absence.
				sellerUUID: draft.sellerUUID.length === 0 ? null : draft.sellerUUID,
				rank: Number(draft.rank),
				refundWindowDays: draft.refundWindowDays.trim() === '' ? null : Number(draft.refundWindowDays),
				clickwrapUUIDs: draft.clickwrapUUIDs,
				optionUUIDs: draft.optionUUIDs,
				// §4.3 — who may read this product's key, written by the server against whichever
				// key the product ends up with (the field above may be blank, in which case it is
				// the slug).
				applicationUUIDs: draft.applicationUUIDs,
				imageUUID: draft.imageUUID,
				entitlementKey: draft.entitlementKey.trim().length === 0 ? undefined : draft.entitlementKey.trim()
			});

			/*
			 * The price is a second call, because it is a second resource — and it is reported
			 * separately for the same reason. If it fails the product still exists, and a toast
			 * claiming the whole thing failed would send the admin to create a duplicate.
			 */
			if (draft.withPrice) {
				try {
					await createPrice({
						productUUID: created.uuid,
						interval: draft.type === 'one_time' ? null : (draft.priceInterval as BillingInterval),
						intervalCount: Number(draft.priceIntervalCount),
						baseCurrency: draft.priceBaseCurrency,
						taxBehavior: draft.priceTaxBehavior as 'inclusive' | 'exclusive',
						trialDays: Number(draft.priceTrialDays),
						isDefault: true,
						amounts: draftPriceAmounts
					});
					toastSuccess(`${name} added, with its first price.`);
				} catch (error) {
					toastError(
						`${name} was created, but its price was not.`,
						`${(error as Error).message} Add one from the product's Prices screen. Until it has one, it cannot be bought.`
					);
				}
			} else {
				toastSuccess(`${name} added.`);
			}

			draft = emptyDraft();
			isAdding = false;
			dispatcher('changed');
		} catch (error) {
			toastError('The product was not added.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnSaveEdit(): Promise<void> {
		if (editing === null) return;

		saving = true;
		try {
			await updateProduct(editing.uuid, {
				name: edit.name.trim(),
				tagline: edit.tagline.trim().length === 0 ? null : edit.tagline.trim(),
				description: edit.description.trim().length === 0 ? null : edit.description.trim(),
				rank: Number(edit.rank),
				highlight: edit.highlight.trim().length === 0 ? null : edit.highlight.trim(),
				comparisonFeatures: cleanComparisonRows(edit.comparisonFeatures),
				isPublic: edit.isPublic,
				...(editing.type === 'one_time' ? { isRepeatable: edit.isRepeatable } : {}),
				// Sent only when it moved: the server refuses any change once somebody holds the
				// product, and an unrelated edit must not meet that refusal over a field it never
				// touched.
				...(Number(edit.seatsPerUnit) !== (editing.seatsPerUnit ?? 1)
					? { seatsPerUnit: Number(edit.seatsPerUnit) }
					: {}),
				sellerUUID: edit.sellerUUID.length === 0 ? null : edit.sellerUUID,
				familySlug: edit.familySlug.length === 0 ? null : edit.familySlug,
				refundWindowDays: edit.refundWindowDays.trim() === '' ? null : Number(edit.refundWindowDays),
				// Empty codes are dropped rather than sent as empty strings: a blank code at the
				// provider is not the same as no code, and the sync would push it as one.
				taxCodes: Object.fromEntries(
					Object.entries(edit.taxCodes).filter(([, code]) => code.trim() !== '')
				),
				clickwrapUUIDs: edit.clickwrapUUIDs,
				optionUUIDs: edit.optionUUIDs,
				applicationUUIDs: edit.applicationUUIDs,
				// Null is a value — it removes the logo — so it is always sent rather than only
				// when set, which would make removing one impossible to express.
				imageUUID: edit.imageUUID,
				postPurchase: postPurchaseInput(edit.postPurchase),
				// Omitted entirely once frozen: the server would refuse a change, and sending the
				// unchanged value back is pointless noise in the audit trail.
				...(isKeyFrozen ? {} : { entitlementKey: edit.entitlementKey.trim() })
			});
			toastSuccess(`${edit.name.trim()} saved.`);
			editingUUID = '';
			dispatcher('changed');
		} catch (error) {
			toastError('The product was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Archiving one product, as a named action rather than a toggle switched off.
	 *
	 * The write is the same `isActive: false` the Active toggle performs — the toggle is the
	 * *state* and this is the *decision*, which is why the confirmation spells out the consequence
	 * the toggle cannot. It matters more now that the row leaves the list: an admin who meant to
	 * change something else and clipped a switch would watch the product disappear.
	 *
	 * Offered only on a **published** product. An unpublished one is a draft, and "archive" is not
	 * what switching a draft off means.
	 */
	async function fnArchive(product: Product): Promise<void> {
		busyUUID = product.uuid;
		try {
			await updateProduct(product.uuid, { isActive: false });
			toastSuccess(
				`${product.name} archived.`,
				'Nobody new can buy it and it is off the storefront. Existing subscriptions keep billing, because archiving only stops new purchases.'
			);
		} catch (error) {
			toastError('The product was not archived.', (error as Error).message);
		} finally {
			busyUUID = '';
			dispatcher('changed');
		}
	}

	async function fnRestore(product: Product): Promise<void> {
		busyUUID = product.uuid;
		try {
			await updateProduct(product.uuid, { isActive: true });
			toastSuccess(`${product.name} restored.`, 'It is back on the storefront and can be bought again.');
		} catch (error) {
			toastError('The product was not restored.', (error as Error).message);
		} finally {
			busyUUID = '';
			dispatcher('changed');
		}
	}

	async function fnPatch(product: Product, changes: { isActive?: boolean; isPublic?: boolean }): Promise<void> {
		busyUUID = product.uuid;
		try {
			await updateProduct(product.uuid, changes);
			toastSuccess(`${product.name} saved.`);
		} catch (error) {
			toastError('The product was not changed.', (error as Error).message);
		} finally {
			busyUUID = '';
			dispatcher('changed');
		}
	}

	function fnCheckedFrom(event: Event): boolean {
		return (event.target as HTMLInputElement).checked;
	}

	function headline(product: Product) {
		return product.prices.find(price => price.isDefault) ?? product.prices[0] ?? null;
	}

	/** One line of the catalogue, or the products that belong to none. */
	interface ProductGroup {
		/** The family slug, or `''` for the standalone group. Also the `{#each}` key. */
		key: string;
		name: string;
		/** The line's own place among the lines (§14) — presentation, never `Product.rank`. */
		displayOrder: number;
		/** The **family's** state, not the products'. An archived line says so once, at its head. */
		isActive: boolean;
		products: Product[];
	}

	/**
	 * The catalogue drawn the way it is actually shaped: lines, each an ordered ladder.
	 *
	 * A flat list in whatever order the API returned made a line something the admin had to
	 * reassemble by eye from a repeated Family cell, and it hid the one relationship the catalogue
	 * turns on — `rank` is what §9.3 reads to decide whether a move between two tiers is an upgrade
	 * or a downgrade, so a line out of rank order on this screen is a ladder whose rungs cannot be
	 * checked against each other.
	 *
	 * Grouped from the **products**, not from `families`: a line with nothing in it belongs on the
	 * Families panel, and an empty heading here would read as a rendering fault.
	 */
	function fnGroup(rows: Product[], lines: ProductFamily[]): ProductGroup[] {
		const collator = new Intl.Collator();
		const groups = new Map<string, ProductGroup>();

		for (const product of rows) {
			const key = product.familySlug ?? '';
			let group = groups.get(key);
			if (group === undefined) {
				group = {
					key,
					name: product.familyName ?? '',
					// Absent on a server older than the field, which is what every line carried
					// before anybody rearranged one.
					displayOrder: lines.find(line => line.slug === key)?.displayOrder ?? 0,
					// Unknown reads as active: the line exists, since a product names it, and
					// drawing it as archived on a list this panel was not given would be a claim
					// about the catalogue made from the absence of a prop.
					isActive: lines.find(line => line.slug === key)?.isActive ?? true,
					products: []
				};
				groups.set(key, group);
			}
			group.products.push(product);
		}

		for (const group of groups.values()) {
			// Rank, then name for the ties. Without the tie-break two products sharing a rank swap
			// places between loads, which reads as the list being edited by somebody else.
			group.products.sort(
				(left, right) => left.rank - right.rank || collator.compare(left.name, right.name)
			);
		}

		return [...groups.values()].sort((left, right) => {
			// The standalone products go last. They are the exception, and a heading for them above
			// the lines would read as the catalogue's first section rather than as its remainder.
			if (left.key === '') return 1;
			if (right.key === '') return -1;
			// The order the store arranged its lines in (§14), so this screen and the storefront
			// read the same. Name is the tie-break, exactly as the server's own read has it.
			return left.displayOrder - right.displayOrder || collator.compare(left.name, right.name);
		});
	}

	/**
	 * The rows the panel draws, with the archived half held back unless it was asked for (§4.1).
	 *
	 * Filtered **before** grouping, so a line whose every product is archived produces no heading
	 * rather than an empty one — `fnGroup` builds the groups from the products for exactly that
	 * reason. A product inside an archived line is caught by its own `isActive`, because archiving
	 * a line deactivates every product in it in the same transaction.
	 *
	 * `isArchivedProduct`, never a bare `!isActive`: a product is created inactive and becomes
	 * published the first time it is switched on, so an unpublished product is a **draft** and not
	 * an archive. Hiding drafts would make a new product vanish from this screen the moment it was
	 * created, which is the one moment its author is certain to be looking for it.
	 */
	$: visibleProducts = showArchived
		? products
		: products.filter(product => !isArchivedProduct(product));
	$: hiddenCount = products.length - visibleProducts.length;

	$: groups = fnGroup(visibleProducts, families);

	/** True while an order is being saved: dragging during the round trip drops onto a stale list. */
	let ordering: boolean = false;

	/**
	 * The order of the tiers inside one line — which **is** `rank` (§9.3), not a cosmetic sort.
	 *
	 * Dragging a tier changes whether a move to it is an upgrade charged now or a downgrade
	 * scheduled for period end, which is why this list is the ladder rather than a view of it. It
	 * is sent as the whole order for the line, and the panel re-reads on success: the server
	 * renumbers every tier in that line, including any the screen did not name.
	 *
	 * Scoped to one group, always. A product dragged into another line has *moved lines* — new
	 * upgrade paths, new terms, a different trial — and that is the Family field on the edit form.
	 * `DragSort` refuses the cross-group drop, and the server refuses it again.
	 */
	async function fnApplyOrder(group: ProductGroup, uuids: string[]): Promise<void> {
		if (ordering) return;

		const previous = products;
		const byUUID = new Map(products.map(product => [product.uuid, product]));
		// Applied locally so the row lands where it was dropped; the reload below replaces it with
		// the server's own order, and a refusal puts the list back exactly as it was.
		products = [
			...products.filter(product => (product.familySlug ?? '') !== group.key),
			...uuids
				.map(uuid => byUUID.get(uuid))
				.filter((product): product is Product => product !== undefined)
				.map((product, index) => ({ ...product, rank: index }))
		];

		ordering = true;
		try {
			await reorderProducts(group.key === '' ? null : group.key, uuids);
			dispatcher('changed');
		} catch (error) {
			products = previous;
			toastError('The order was not saved.', (error as Error).message);
		} finally {
			ordering = false;
		}
	}

	/** The group a row belongs to, found from the row rather than assumed by the caller. */
	function fnGroupOf(uuid: string): ProductGroup | undefined {
		return groups.find(group => group.products.some(product => product.uuid === uuid));
	}

	function fnDropped(key: string, targetKey: string): void {
		const group = fnGroupOf(key);
		// A drop onto another line is refused rather than reparenting the product: `DragSort` will
		// not report one, and this is the second guard for a list that re-rendered mid-drag.
		if (group === undefined || group !== fnGroupOf(targetKey)) return;

		const uuids = group.products.map(product => product.uuid);
		void fnApplyOrder(group, moveKeyBefore(uuids, key, targetKey));
	}

	function fnMoved(uuid: string, direction: number): void {
		const group = fnGroupOf(uuid);
		if (group === undefined) return;

		const uuids = group.products.map(product => product.uuid);
		const next = moveKeyBy(uuids, uuid, direction);
		// Nothing moved — the entry tier pressed up, or the top tier pressed down. Sending it would
		// be a write with no change in it, and an audit entry saying the ladder was rearranged.
		if (next.every((value, index) => value === uuids[index])) return;
		void fnApplyOrder(group, next);
	}

	/** Columns the group heading spans. Kept beside the `TableHead` it has to agree with. */
	const COLUMN_COUNT = 9;
</script>

<Panel title="Products" faIcon="box">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (isAdding = !isAdding)}>
			<i class="fa-solid fa-plus mr-2" />Add product
		</Button>
	</svelte:fragment>

	{#if loading}
		<EmptyState variant="loading" title="Loading products…" />
	{:else if products.length === 0}
		<EmptyState
			title="No product yet."
			description="The store stays closed until one active product has an active price."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first product</Button>
		</EmptyState>
	{:else if visibleProducts.length === 0}
		<!--
			Everything the store has is archived. Its own state rather than the one above: "No
			product yet" over a store that has retired eleven of them invites an admin to author a
			product they already have, and hides the fact that there is anything to restore.
		-->
		<EmptyState
			title="Every product is archived."
			description="Turn on “Show archived” above to see them, or add a new product."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add a product</Button>
		</EmptyState>
	{:else}
		<!--
			Drag to reorder within a line (§14). One wrapper for the whole table rather than one per
			group: a `<div>` cannot sit inside a `<tbody>`, and the rows carry the line they belong
			to so a drag never crosses one.
		-->
		<DragSort disabled={ordering} on:reorder={event => fnDropped(event.detail.key, event.detail.targetKey)} let:overKey let:draggingKey>
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<!-- The grip's column. Headed blank: it labels an action, not a field. -->
				<TableHeadCell class="w-8" />
				<TableHeadCell>Product</TableHeadCell>
				<!--
					No Family column: it is the heading each group is under, and repeating it on every
					row is the same fact rendered twice — which is both noise and the thing that lets
					two renderings of one fact drift.
				-->
				<TableHeadCell>Rank</TableHeadCell>
				<TableHeadCell>Entitlement key</TableHeadCell>
				<TableHeadCell>Price</TableHeadCell>
				<TableHeadCell>Terms</TableHeadCell>
				<TableHeadCell>Public</TableHeadCell>
				<TableHeadCell>Active</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each groups as group (group.key)}
					<!--
						The line's heading. A row rather than a separate table, so every tier in the
						catalogue still lines up on one set of columns — a table per family would put
						each line's prices and toggles on their own private grid.
					-->
					<TableBodyRow class="bg-depth">
						<TableBodyCell colspan={COLUMN_COUNT} class="dataTable !py-2">
							<div class="flex flex-wrap items-center gap-2">
								{#if group.key === ''}
									<i class="fa-solid fa-box text-xs opacity-60" aria-hidden="true" />
									<!-- Named, not left blank: a heading with no words reads as a bug, and
									     these products are a real category rather than leftovers. -->
									<span class="card-label !mb-0">Not in a family</span>
								{:else}
									<i class="fa-solid fa-layer-group text-xs opacity-60" aria-hidden="true" />
									<span class="card-label !mb-0">{group.name}</span>
									<span class="text-xs text-default font-mono">{group.key}</span>
									{#if !group.isActive}
										<!-- Said once, at the head. Every product in an archived line is
										     inactive, so a badge per row would be the same fact eight times. -->
										<Badge color="dark">Archived</Badge>
									{/if}
								{/if}
								<span class="text-xs text-default">
									{group.products.length}
									{group.products.length === 1 ? 'product' : 'products'}
								</span>
							</div>
						</TableBodyCell>
					</TableBodyRow>

					{#each group.products as product (product.uuid)}
					<TableBodyRow
						data-sort-key={product.uuid}
						data-sort-group={group.key}
						class={[
							selectedProductUUID === product.uuid ? 'bg-primary-50 dark:bg-primary-900/20' : '',
							overKey === product.uuid ? 'border-t-2 border-t-primary-500' : '',
							draggingKey === product.uuid ? 'opacity-50' : ''
						]
							.filter(Boolean)
							.join(' ')}
					>
						<TableBodyCell class="dataTable !px-2">
							<!--
								The grip moves `rank`, which is what §9.3 reads — so the label says
								which tier is being moved rather than "drag handle".
							-->
							<DragHandle
								label={product.name}
								disabled={ordering}
								on:move={event => fnMoved(product.uuid, event.detail.direction)}
							/>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<!--
								The logo beside the name, not in a column of its own: most products have
								none, and a dedicated column would reserve width on every row for an
								empty cell — which reads as missing data rather than as a product with no
								logo. Beside the name it is doing a logo's job, which is identifying the
								row at a glance on a screen that is otherwise all text.

								Through `mediaURL`, because the server hands out a server-relative path
								and this client is pointed at a separate backend origin.
							-->
							<div class="flex items-start gap-2.5">
								{#if product.imageURL}
									<img
										src={mediaURL(product.imageURL)}
										alt=""
										class="w-8 h-8 rounded-lg object-contain shrink-0 mt-0.5"
										loading="lazy"
									/>
								{/if}
								<div class="min-w-0">
							<div class="flex flex-wrap items-center gap-2">
								<span class="font-semibold" data-testid="product-name">{product.name}</span>
								<!--
									Only reachable with "Show archived" on. In words beside the name rather
									than left to the dimmed Active toggle at the far end of the row: this is
									a list somebody has deliberately asked to see the retired half of, and
									the two halves have to be told apart at a glance.
								-->
								{#if isArchivedProduct(product)}
									<Badge color="dark">Archived</Badge>
								{:else if !product.isActive}
									<!--
										Inactive but never published — a draft, not an archive. It stays on
										the list whatever the switch says, so naming the state is what stops
										it reading as an archived row that failed to be hidden.
									-->
									<Badge color="yellow">Draft</Badge>
								{/if}
							</div>
							<div class="text-xs text-default font-mono">{product.slug}</div>
							{#if syncStatus.has(product.uuid)}
								{@const state = syncStatus.get(product.uuid)}
								{#if state && state.syncStatus !== 'synced'}
									<div class="text-xs {syncCopy[state.syncStatus].classes}" data-testid="product-sync-status">
										<i class="fa-solid fa-triangle-exclamation mr-1" aria-hidden="true" />{syncCopy[
											state.syncStatus
										].label}{#if state.syncError}: {state.syncError}{/if}
									</div>
								{/if}
							{/if}
								</div>
							</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">{product.rank}</TableBodyCell>
						<TableBodyCell class="dataTable">
							<span class="font-mono text-xs">{product.entitlementKey}</span>
							{#if product.publishedAt !== null}
								<Badge color="yellow" class="ml-2"><i class="fa-solid fa-lock mr-1" />Frozen</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<PriceDisplay
								price={headline(product)}
								{currency}
								currencyCode={product.currency}
								size="sm"
								showTaxNote={false}
								showTrial={false}
							/>
						</TableBodyCell>
						<!--
							What a buyer of this product has to tick. Both halves of the union, with the
							inherited ones marked: only the product's own terms can be changed from this
							row, and an admin who cannot tell them apart will try to untick the others.
						-->
						<TableBodyCell class="dataTable">
							{@const terms = fnTermsFor(product)}
							{#if terms.own.length === 0 && terms.inherited.length === 0}
								<span class="text-default">—</span>
							{:else}
								<div class="flex flex-wrap gap-1 max-w-[16rem]">
									{#each terms.own as term (term.uuid)}
										<Badge color="dark" class={term.isActive ? '' : 'opacity-60'}>
											{term.title}{#if !term.isActive}&nbsp;(inactive){/if}
										</Badge>
									{/each}
									{#each terms.inherited as term (term.uuid)}
										<!-- Editable on the line, not here — so it says where it comes from. -->
										<Badge
											color="none"
											class="border border-gray-300 dark:border-zinc-600 {term.isActive ? '' : 'opacity-60'}"
											title={`From the ${product.familyName} family`}
										>
											<i class="fa-solid fa-layer-group mr-1 text-[9px]" aria-hidden="true" />{term.title}{#if !term.isActive}&nbsp;(inactive){/if}
										</Badge>
									{/each}
								</div>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<Toggle
								checked={product.isPublic}
								disabled={busyUUID === product.uuid}
								on:change={event => fnPatch(product, { isPublic: fnCheckedFrom(event) })}
							/>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<Toggle
								checked={product.isActive}
								disabled={busyUUID === product.uuid}
								on:change={event => fnPatch(product, { isActive: fnCheckedFrom(event) })}
							/>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex flex-wrap justify-end gap-2">
								<Button size="xs" color="alternative" on:click={() => fnStartEdit(product)}>Edit</Button>
								<Button size="xs" color="alternative" on:click={() => (selectedProductUUID = product.uuid)}>
									Prices
								</Button>
								<!--
									Archive is offered on a published product only: switching a draft off is
									not archiving it, and calling it that would teach the wrong word for the
									state the row is actually in.
								-->
								{#if isArchivedProduct(product)}
									<Button
										size="xs"
										color="alternative"
										disabled={busyUUID === product.uuid}
										on:click={() => fnRestore(product)}
									>
										<i class="fa-solid fa-rotate-left mr-1" />Restore
									</Button>
								{:else if product.isActive && product.publishedAt !== null}
									<ConfirmButton
										size="xs"
										color="red"
										disabled={busyUUID === product.uuid}
										confirmText={`Archive “${product.name}”? It comes off the storefront and nobody new can buy it. Existing subscriptions keep billing.`}
										on:confirm={() => fnArchive(product)}
									>
										<i class="fa-solid fa-box-archive mr-1" />Archive
									</ConfirmButton>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
					{/each}
				{/each}
			</TableBody>
		</Table>
		</DragSort>
	{/if}

	<FormModal
		bind:open={isAdding}
		title="New product"
		faIcon="plus"
		submitLabel="Add product"
		size="lg"
		busy={saving}
		canSubmit={canCreate}
		description="New products start inactive. Activating one publishes it and freezes its entitlement key."
		on:submit={fnCreate}
	>
			<div class="grid md:grid-cols-3 gap-4">
				<FormField id="product-slug" label="Slug" required description="The URL segment." error={slugError}>
					<Input id="product-slug" bind:value={draft.slug} placeholder="polyverse-pro" disabled={saving} />
				</FormField>
				<FormField id="product-name" label="Name" required>
					<Input id="product-name" bind:value={draft.name} placeholder="Polyverse Pro" disabled={saving} />
				</FormField>
				<FormField
					id="product-type"
					label="Type"
					required
					description="A product is one or the other, never both."
				>
					<SelectField
						id="product-type"
						bind:value={draft.type}
						disabled={saving}
						items={[
							{ value: 'subscription', name: 'Subscription' },
							{ value: 'one_time', name: 'One-time purchase' }
						]}
					/>
				</FormField>
				<FormField id="product-family" label="Family" description="Tiers a customer can move between.">
					<SelectField id="product-family" bind:value={draft.familySlug} items={familyItems} disabled={saving} />
				</FormField>
				<FormField
					id="product-seller"
					label="Sold by"
					description="The legal entity that invoices it. Changing this affects future invoices only."
				>
					<SelectField id="product-seller" bind:value={draft.sellerUUID} items={sellerItems} disabled={saving} />
				</FormField>
				<FormField id="product-rank" label="Rank" description="Higher is more capable. Drives upgrade vs downgrade.">
					<Input id="product-rank" type="number" min="0" step="1" bind:value={draft.rank} disabled={saving} />
				</FormField>
				<FormField
					id="product-key"
					label="Entitlement key"
					description="Defaults to the slug. Frozen once the product is first activated."
					helpText="The only string consuming applications hard-code. Every entitlement issued carries it, so it cannot change after publication without stranding existing holders."
				>
					<Input id="product-key" bind:value={draft.entitlementKey} placeholder="polyverse_pro" disabled={saving} />
				</FormField>
			</div>

			<!--
				The presentation fields, on the create form rather than only on the edit form.
				Reaching them used to mean creating the product and then editing it, so the first
				thing every new product did was exist with no tagline, no description and no
				features — and the storefront renders exactly that.
			-->
			<FormField label="Logo" description="Shown on the storefront beside the name.">
				<ImageField bind:uuid={draft.imageUUID} bind:url={draft.imageURL} disabled={saving} label="Product logo" />
			</FormField>

			<FormField id="product-tagline" label="Tagline" description="One line under the name on the storefront.">
				<Input id="product-tagline" bind:value={draft.tagline} placeholder="For working artists" disabled={saving} />
			</FormField>

			<FormField id="product-description" label="Description" description="HTML. Rendered inside `.richtext`.">
				<Textarea id="product-description" bind:value={draft.description} rows={4} disabled={saving} />
			</FormField>

			<FormField
				id="product-highlight"
				label="Promoted badge"
				description="Blank for none. Shown on this tier's column and on its page."
				helpText="Deliberately not derived from rank: the tier a store promotes and the tier that is most capable are routinely not the same one."
			>
				<Input id="product-highlight" bind:value={draft.highlight} placeholder="Most popular" maxlength="60" disabled={saving} />
			</FormField>

			<FormField
				label="What this tier gives"
				description="One row each. A blank value renders as a tick; an amount renders as itself."
				helpText="The whole storefront is drawn from these: the comparison table, the catalogue card and the product page. The row name is what the columns are aligned on, so tiers describing one capability must spell it identically; a row this tier omits renders as a dash against the tiers that have it."
			>
				<ComparisonFeatureEditor
					id="product-comparison"
					bind:rows={draft.comparisonFeatures}
					knownRows={draftSiblingRows}
					disabled={saving}
				/>
			</FormField>

			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					id="product-refund"
					label="Refund window (days)"
					description="Blank uses the store default."
				>
					<Input id="product-refund" type="number" min="0" step="1" bind:value={draft.refundWindowDays} disabled={saving} />
				</FormField>
				<FormField label="Listed publicly" description="Unlisted products stay reachable by direct link.">
					<Toggle bind:checked={draft.isPublic} disabled={saving} />
				</FormField>

				<!--
					§4.1. A property of the product rather than a choice at checkout: every unit bought
					carries this many seats, which is how many machines one licence activates on. It
					sits beside the refund window because both are terms of the sale rather than copy.
				-->
				<FormField
					id="product-seats-per-unit"
					label="Seats per unit"
					description="How many seats each unit bought carries — for a licence, how many machines it activates on. Ignored on a per-seat price, where each seat is bought on its own. Cannot change once anybody holds the product."
					error={seatsPerUnitProblem(draft.seatsPerUnit)}
				>
					<Input
						id="product-seats-per-unit"
						type="text"
						inputmode="numeric"
						bind:value={draft.seatsPerUnit}
						disabled={saving}
					/>
				</FormField>

				<!--
					Only for a one-time product, because it means nothing anywhere else: buying a
					subscription you already hold is a plan change, and the server refuses the field
					on one by name (§4.1).
				-->
				{#if draft.type === 'one_time'}
					<FormField
						id="product-repeatable"
						label="Can be bought repeatedly"
						description="For anything sold by the unit, like a credit pack or an extra slot. Each purchase adds to the quantity applications see; left off, buying it twice is refused as a duplicate."
					>
						<Toggle id="product-repeatable" bind:checked={draft.isRepeatable} disabled={saving} />
					</FormField>
				{/if}
			</div>

			{#if clickwraps.length > 0}
				<FormField
					label="Purchase terms"
					description="The buyer must accept every ticked term before this can be bought. Terms on the family apply too."
				>
					<div class="space-y-1">
						{#each clickwraps as term (term.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={draft.clickwrapUUIDs.includes(term.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(draft.clickwrapUUIDs = fnToggleTerm(draft.clickwrapUUIDs, term.uuid, event))}
								/>
								{term.title}
								{#if !term.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}

			{#if productOptions.length > 0}
				<FormField
					label="Product options"
					description="Offered to the buyer, never required. Options on the family are offered too."
				>
					<div class="space-y-1">
						{#each productOptions as option (option.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={draft.optionUUIDs.includes(option.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(draft.optionUUIDs = fnToggleTerm(draft.optionUUIDs, option.uuid, event))}
								/>
								{option.title}
								{#if !option.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}


			{#if applications.length > 0}
				<FormField
					label="Applications that read this"
					description="Ticked applications may read this product's entitlement key on the API. An application with no keys sees nothing at all."
				>
					<div class="space-y-1">
						{#each scopedApplications as application (application.abstractIDAppUUID)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={draft.applicationUUIDs.includes(application.abstractIDAppUUID)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(draft.applicationUUIDs = fnToggleTerm(
											draft.applicationUUIDs,
											application.abstractIDAppUUID,
											event
										))}
								/>
								{application.name}
								{#if !application.isActive}
									<span class="text-default text-xs">(inactive)</span>
								{/if}
							</label>
						{/each}
						{#each allKeysApplications as application (application.abstractIDAppUUID)}
							<!--
								Ticked and immovable. The `allKeys` flag is what grants it the key, not
								the list below it, so a control here would either change nothing or read
								as withdrawing access that nothing withdrew.
							-->
							<label class="flex items-center gap-2 text-sm opacity-70">
								<Checkbox checked={true} disabled spacing="" class="shrink-0" />
								{application.name}
								<span class="text-default text-xs">reads every key</span>
							</label>
						{/each}
					</div>
				</FormField>
			{/if}
			<!--
				The first price, in the same form. A product with no price is unbuyable and the store
				stays shut until one product has one, so pricing on a separate screen means every new
				product spends time in a state somebody has to remember to leave.
			-->
			<div class="widget bg-depth space-y-4">
				<label class="flex items-center gap-2 text-sm font-medium">
					<Checkbox bind:checked={draft.withPrice} disabled={saving} spacing="" class="shrink-0" />
					Give it a price now
				</label>

				{#if draft.withPrice}
					<div class="grid md:grid-cols-3 gap-4">
						{#if draft.type === 'subscription'}
							<FormField id="product-price-interval" label="Interval" required>
								<SelectField
									id="product-price-interval"
									bind:value={draft.priceInterval}
									items={BILLING_INTERVALS}
									disabled={saving}
								/>
							</FormField>
							<FormField id="product-price-count" label="Interval count" description="Bill every N intervals.">
								<Input id="product-price-count" type="number" min="1" step="1" bind:value={draft.priceIntervalCount} disabled={saving} />
							</FormField>
							<FormField id="product-price-trial" label="Trial days" description="0 for none.">
								<Input id="product-price-trial" type="number" min="0" step="1" bind:value={draft.priceTrialDays} disabled={saving} />
							</FormField>
						{/if}
						<FormField
							id="product-price-tax"
							label="Tax behaviour"
							required
							description="Exclusive adds tax at checkout; inclusive means the amount already contains it."
						>
							<SelectField
								id="product-price-tax"
								bind:value={draft.priceTaxBehavior}
								disabled={saving}
								items={[
									{ value: 'exclusive', name: 'Exclusive (tax added at checkout)' },
									{ value: 'inclusive', name: 'Inclusive (tax already in the amount)' }
								]}
							/>
						</FormField>
						<FormField id="product-price-base" label="Base currency" required description="Must be one of the amounts below.">
							<CurrencyPicker
								id="product-price-base"
								label="Base currency"
								{currencies}
								bind:value={draft.priceBaseCurrency}
								disabled={saving}
							/>
						</FormField>
					</div>

					<div class="grid md:grid-cols-3 gap-4">
						{#each currencies as row (row.code)}
							<FormField
								id={`product-price-amount-${row.code}`}
								label={`${row.code.toUpperCase()} (${row.name})`}
								description={`As charged: ${fnFormatDraftAmount(row.code)}`}
							>
								<!-- Text, not `type="number"`: a number input rejects the comma half of
								     Europe types, and its spinner steps a whole unit at a time. -->
								<Input
									id={`product-price-amount-${row.code}`}
									type="text"
									inputmode="decimal"
									placeholder={majorUnitsFrom(0, row.minorUnitDigits)}
									bind:value={draft.priceAmounts[row.code]}
									disabled={saving}
								/>
							</FormField>
						{/each}
					</div>
					<p class="text-xs text-default">
						Leave a currency blank to leave the product unavailable in it, and the storefront says so
						rather than quoting another currency. Prices are immutable once a customer or a provider
						depends on them; until then this one stays editable.
					</p>
				{/if}
			</div>
	</FormModal>

	<!-- Editing sits over the table, so the row being edited stays in view. -->
	{#if editing !== null}
		<FormModal
			open={true}
			title={`Edit ${editing.name}`}
			faIcon="pen"
			submitLabel="Save"
			size="lg"
			busy={saving}
			canSubmit={seatsPerUnitProblem(edit.seatsPerUnit) === '' && postPurchaseValid}
			on:submit={fnSaveEdit}
			on:cancel={() => (editingUUID = '')}
		>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField id="edit-name" label="Name" required>
					<Input id="edit-name" bind:value={edit.name} disabled={saving} />
				</FormField>
				<FormField id="edit-tagline" label="Tagline">
					<Input id="edit-tagline" bind:value={edit.tagline} disabled={saving} />
				</FormField>
				<FormField id="edit-rank" label="Rank" description="Higher is more capable (§9.3).">
					<Input id="edit-rank" type="number" min="0" step="1" bind:value={edit.rank} disabled={saving} />
				</FormField>

				{#if isKeyFrozen}
					<!-- Write-once, with the reason. A greyed-out field with no explanation reads
					     as a bug; this one states what it would break. -->
					<FormField
						id="edit-key"
						label="Entitlement key"
						description={`Frozen since publication on ${formatFriendlyDate(editing.publishedAt ?? '')}. Every entitlement already issued carries this key, and applications hard-code it, so changing it would strand existing holders on the old key while new purchasers got the new one. Create a new product instead.`}
					>
						<Input id="edit-key" value={editing.entitlementKey} readonly disabled class="font-mono" />
					</FormField>
				{:else}
					<FormField
						id="edit-key"
						label="Entitlement key"
						description="Editable until this product is first activated. After that it is frozen."
					>
						<Input id="edit-key" bind:value={edit.entitlementKey} disabled={saving} class="font-mono" />
					</FormField>
				{/if}

				<FormField
					id="edit-seller"
					label="Sold by"
					description="The legal entity that invoices it. Affects future invoices only: every invoice already issued carries the seller it was issued with."
				>
					<SelectField id="edit-seller" bind:value={edit.sellerUUID} items={sellerItems} disabled={saving} />
				</FormField>

				<FormField
					id="edit-family"
					label="Family"
					description="Tiers a customer can move between. Moving a product between families changes what counts as an upgrade."
				>
					<SelectField id="edit-family" bind:value={edit.familySlug} items={familyItems} disabled={saving} />
				</FormField>

				<FormField
					id="edit-refund-window"
					label="Refund window (days)"
					description="Empty uses the store default from Settings."
				>
					<Input
						id="edit-refund-window"
						type="number"
						min="0"
						step="1"
						bind:value={edit.refundWindowDays}
						disabled={saving}
					/>
				</FormField>

				<!--
					§4.1. A property of the product rather than a choice at checkout: every unit bought
					carries this many seats, which is how many machines one licence activates on. It
					sits beside the refund window because both are terms of the sale rather than copy.
				-->
				<FormField
					id="edit-seats-per-unit"
					label="Seats per unit"
					description="How many seats each unit bought carries — for a licence, how many machines it activates on. Ignored on a per-seat price. Refused once anybody holds the product, because every holder would be re-licensed at the new figure."
					error={seatsPerUnitProblem(edit.seatsPerUnit)}
				>
					<Input
						id="edit-seats-per-unit"
						type="text"
						inputmode="numeric"
						bind:value={edit.seatsPerUnit}
						disabled={saving}
					/>
				</FormField>

				{#if editing?.type === 'one_time'}
					<FormField
						id="edit-repeatable"
						label="Can be bought repeatedly"
						description="Each purchase adds to the quantity applications see. Turning it off refuses a further purchase to anyone who already holds it; what has already been bought is untouched."
					>
						<Toggle id="edit-repeatable" bind:checked={edit.isRepeatable} disabled={saving} />
					</FormField>
				{/if}
			</div>

			{#if providerTypes.length > 0}
				<!--
					One field per registered adapter, because a tax code is provider vocabulary: it
					is Stripe's `txcd_…` string, not ours, and a second provider would want its own.
				-->
				<div class="grid md:grid-cols-2 gap-4">
					{#each providerTypes as providerType (providerType)}
						<FormField
							id={`edit-tax-code-${providerType}`}
							label={`Tax code (${providerType})`}
							description="The provider's own product tax category. Left empty, the provider applies its account default."
						>
							<Input
								id={`edit-tax-code-${providerType}`}
								bind:value={edit.taxCodes[providerType]}
								placeholder="txcd_10103100"
								disabled={saving}
								class="font-mono"
							/>
						</FormField>
					{/each}
				</div>
			{/if}

			{#if clickwraps.length > 0}
				<!--
					Ticking a term here attaches this product to it. A term covering several plans
					keeps its other plans and families — this edits one row of the term's scope.
				-->
				<FormField
					label="Purchase terms"
					description="The buyer must accept every ticked term before this can be bought. Terms on the family apply too, and are managed on the family."
				>
					<div class="space-y-1">
						{#each clickwraps as term (term.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.clickwrapUUIDs.includes(term.uuid)}
									disabled={saving}
									on:change={event =>
										(edit.clickwrapUUIDs = fnToggleTerm(edit.clickwrapUUIDs, term.uuid, event))}
								/>
								{term.title}
								{#if !term.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}

			{#if productOptions.length > 0}
				<!--
					§4.8. Ticking an option here offers it on this product; the option keeps its
					other plans and lines — this edits one row of its scope.
				-->
				<FormField
					label="Product options"
					description="Offered to the buyer, never required. Options on the family are offered too, and are managed on the family."
				>
					<div class="space-y-1">
						{#each productOptions as option (option.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.optionUUIDs.includes(option.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.optionUUIDs = fnToggleTerm(edit.optionUUIDs, option.uuid, event))}
								/>
								{option.title}
								{#if !option.isActive}
									<span class="text-default text-xs">(retired)</span>
								{/if}
							</label>
						{/each}
					</div>
				</FormField>
			{/if}


			{#if applications.length > 0}
				<FormField
					label="Applications that read this"
					description="Ticked applications may read this product's entitlement key on the API. An application with no keys sees nothing at all."
				>
					<div class="space-y-1">
						{#each scopedApplications as application (application.abstractIDAppUUID)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.applicationUUIDs.includes(application.abstractIDAppUUID)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.applicationUUIDs = fnToggleTerm(
											edit.applicationUUIDs,
											application.abstractIDAppUUID,
											event
										))}
								/>
								{application.name}
								{#if !application.isActive}
									<span class="text-default text-xs">(inactive)</span>
								{/if}
							</label>
						{/each}
						{#each allKeysApplications as application (application.abstractIDAppUUID)}
							<!--
								Ticked and immovable. The `allKeys` flag is what grants it the key, not
								the list below it, so a control here would either change nothing or read
								as withdrawing access that nothing withdrew.
							-->
							<label class="flex items-center gap-2 text-sm opacity-70">
								<Checkbox checked={true} disabled spacing="" class="shrink-0" />
								{application.name}
								<span class="text-default text-xs">reads every key</span>
							</label>
						{/each}
					</div>
				</FormField>
			{/if}
			<FormField label="Logo" description="Shown on the storefront beside the name.">
				<ImageField bind:uuid={edit.imageUUID} bind:url={edit.imageURL} disabled={saving} label="Product logo" />
			</FormField>

			<FormField id="edit-description" label="Description" description="HTML. Rendered inside `.richtext`.">
				<Textarea id="edit-description" bind:value={edit.description} rows={4} disabled={saving} />
			</FormField>

			<FormField
				id="edit-highlight"
				label="Promoted badge"
				description="Blank for none. Shown on this tier's column and on its page."
				helpText="Deliberately not derived from rank: the tier a store promotes and the tier that is most capable are routinely not the same one."
			>
				<Input id="edit-highlight" bind:value={edit.highlight} placeholder="Most popular" maxlength="60" disabled={saving} />
			</FormField>

			<FormField
				label="What this tier gives"
				description="One row each. A blank value renders as a tick; an amount renders as itself."
				helpText="The whole storefront is drawn from these: the comparison table, the catalogue card and the product page. The row name is what the columns are aligned on, so tiers describing one capability must spell it identically; a row this tier omits renders as a dash against the tiers that have it."
			>
				<ComparisonFeatureEditor
					id="edit-comparison"
					bind:rows={edit.comparisonFeatures}
					knownRows={editSiblingRows}
					disabled={saving}
				/>
			</FormField>

			<FormField
				id="edit-post-purchase-message"
				label="After purchase"
				description="Shown the moment a purchase of this product completes — a thank-you and where to go next. Leave empty to use the product family's."
			>
				<PostPurchaseEditor id="edit-post-purchase" bind:value={edit.postPurchase} bind:valid={postPurchaseValid} disabled={saving} />
			</FormField>
		</FormModal>
	{/if}
</Panel>
